/**
 * Alertes livreur — couche push (app fermée) + couche in-app (son/vibration).
 *
 * Push : service worker dédié /push-sw.js (scope /push/), clés VAPID côté serveur.
 * Son : Web Audio API (aucun fichier audio requis) — appeler unlockAudio() sur
 *       un geste utilisateur pour lever la politique d'autoplay des navigateurs.
 */
import { apiClient } from "@/lib/api";

const SW_URL = "/push-sw.js";
const SW_SCOPE = "/push/";

export function pushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

async function getPushRegistration() {
  const reg = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
  if (reg.active) return reg;
  const sw = reg.installing || reg.waiting;
  if (sw) {
    await new Promise((resolve) => {
      const onState = () => {
        if (sw.state === "activated") {
          sw.removeEventListener("statechange", onState);
          resolve();
        }
      };
      sw.addEventListener("statechange", onState);
      onState();
    });
  }
  return reg;
}

/** "unsupported" | "denied" | "subscribed" | "idle" */
export async function getPushState() {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    return sub ? "subscribed" : "idle";
  } catch {
    return "idle";
  }
}

export async function enablePushAlerts() {
  if (!pushSupported()) {
    throw new Error("Notifications non supportées — sur iPhone, installez d'abord l'app sur l'écran d'accueil");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Permission refusée — autorisez les notifications dans les réglages du navigateur");
  }
  const { data } = await apiClient.get("/push/vapid-public-key");
  const reg = await getPushRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }
  await apiClient.post("/push/subscribe", sub.toJSON());
  return true;
}

export async function disablePushAlerts() {
  const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    try {
      await apiClient.post("/push/unsubscribe", { endpoint: sub.endpoint });
    } catch {}
    await sub.unsubscribe();
  }
  return true;
}

/**
 * Self-healing re-registration: if the user already granted notifications,
 * silently (re)subscribe and re-POST the subscription to the server.
 * Fixes the "green bell but server lost the subscription" drift — call it
 * on dashboard mount (driver + restaurant).
 */
export async function ensurePushSubscription() {
  try {
    if (!pushSupported() || Notification.permission !== "granted") return false;
    const { data } = await apiClient.get("/push/vapid-public-key");
    const reg = await getPushRegistration();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
    }
    await apiClient.post("/push/subscribe", sub.toJSON());
    return true;
  } catch {
    return false;
  }
}

// ---------- Couche in-app : son + vibration ----------
let audioCtx = null;

export function unlockAudio() {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
  } catch {}
}

export function playAlertSound(kind = "priority") {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
    const now = audioCtx.currentTime;
    const notes = kind === "priority" ? [880, 1174.66, 880, 1174.66] : [659.25, 880];
    notes.forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t = now + i * 0.18;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.4, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(t);
      osc.stop(t + 0.17);
    });
  } catch {}
}

export function vibrate(pattern) {
  try {
    if (navigator.vibrate) navigator.vibrate(pattern);
  } catch {}
}
